import { t } from "i18next";

import type {
  BaseFormTemplate,
  ExtendedThalamosUser,
  GetFormDraftResponse,
  SignDraftRequest,
  SupportingFormsDetails,
} from "@thalamos/aspire-common";
import {
  applicationTemplateIds,
  baseTemplates,
  canDoctorInitiateS12Payments,
  getFullFormName,
  getLatestVersion,
  isIndependentUserSession,
  medicalRecommendationTemplateIds,
  recordOfDetentionTemplateIds,
  RequirementFriendlyDescription,
} from "@thalamos/aspire-common";

import { api } from "../../../api.js";
import { routeFns } from "../../../routePaths.js";
import {
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import type { MhlAutoRouteTeam } from "~/components/form/SignatureDialog/SignatureDialog.js";
import {
  queueS132Prompt,
  signCallsForS132Prompt,
} from "~/components/s132/s132PromptQueue.js";

interface Props {
  signature?: string | undefined;
  requiresSupportingForms?: any;
  isSupportingFormsProvided?: boolean;
  allSelectedForms?: string[];
  reason?: string;
  formId: string;
  navigate: any;
  formDraft: GetFormDraftResponse<any>;
  template: BaseFormTemplate;
  requiresSignature: boolean;
  saveDefaultSignature: boolean;
  user: ExtendedThalamosUser;
  mhlAutoRouteTeams?: MhlAutoRouteTeam[];
}

function getBannerDescriptionOfFriendlyRequirement(
  friendlyRequirement: RequirementFriendlyDescription,
) {
  switch (friendlyRequirement) {
    case RequirementFriendlyDescription.registeredNurse:
      return "the registered nurse";
    case RequirementFriendlyDescription.onBehalfOfResponsibleHospital:
      return "someone acting on behalf of the responsible hospital";
    case RequirementFriendlyDescription.onBehalfOfResponsibleHospitalOrLocalAuthority:
      return "someone acting on behalf of the responsible hospital or the local social services authority";
    case RequirementFriendlyDescription.professionalConcernedWithPatientCareDifferentProfessionFromResponsibleClinician:
      return "a professional who has been professionally concerned with the patient's medical treatment and who is of a different profession from the responsible clinician";
    case RequirementFriendlyDescription.approvedMentalHealthProfessional:
      return "the approved mental health professional";
    case RequirementFriendlyDescription.responsibleClinician:
      return "the responsible clinician ";
    case RequirementFriendlyDescription.approvedClinicianOrMedicalPractitionerUnderSection52:
      return "a medical practitioner or an approved clinician qualified to do so under section 5(2) of the Act ";
    case RequirementFriendlyDescription.approvedClinicianOrRegisteredMedicalPractitioner:
      return "the approved clinician or a registered medical practitioner";
    case RequirementFriendlyDescription.registeredMedicalPractitioner:
      return "a registered medical practitioner";
    case RequirementFriendlyDescription.approvedClinician:
      return "the approved clinician";
  }
}

export const onHappyWithSignature = async ({
  signature,
  requiresSupportingForms,
  isSupportingFormsProvided,
  allSelectedForms,
  reason,
  formId,
  navigate,
  formDraft,
  template,
  requiresSignature,
  saveDefaultSignature,
  user,
  mhlAutoRouteTeams,
}: Props) => {
  // Check if MHL auto-routing will happen (Part 1 signature with configured teams)
  const willAutoRoute =
    formDraft.part === 1 &&
    formDraft.formVersion === 1 &&
    mhlAutoRouteTeams &&
    mhlAutoRouteTeams.length > 0;

  if (signature || !requiresSignature) {
    let supportingFormsDetails: SupportingFormsDetails | undefined = undefined;

    if (requiresSupportingForms) {
      if (!isSupportingFormsProvided) {
        supportingFormsDetails = {
          type: "provided",
          formIds: allSelectedForms!,
        };
      } else if (allSelectedForms && allSelectedForms.length > 0) {
        // Some med recs linked digitally, others marked with a reason (e.g. "completedOnPaper")
        supportingFormsDetails = {
          type: "partially-provided",
          formIds: allSelectedForms,
          reason: reason!,
        };
      } else {
        supportingFormsDetails = {
          type: "not-provided",
          reason: reason!,
        };
      }
    }

    const signDraftRequest: SignDraftRequest = {
      draftId: formDraft.id,
      signatureBase64: signature,
      supportingFormsDetails: supportingFormsDetails,
    };

    const result = await api.forms.sign(formId, signDraftRequest);

    if (result.status === 204) {
      if (saveDefaultSignature && !user.defaultSignature)
        await api.users.update(user.id, {
          defaultSignature: signature,
        });

      const formSuccessfullySignedTitle = "Form successfully signed";

      renderSuccessToast({ message: formSuccessfullySignedTitle });

      const fullFormName = getFullFormName(template);
      const partText = `part ${formDraft.part}`;
      const formAndPartName = `${partText} of ${fullFormName}`;
      const isMultiPart = template.parts.length > 1;

      const isH3Form = formDraft.formTemplate.id === "mha-h3";
      const isSectionValue2or3 = ["2", "3"].includes(
        formDraft.data.sectionValue,
      );

      const isOnePartH3Form = isH3Form && isSectionValue2or3;

      const isASection4H3 = isH3Form && formDraft.data.sectionValue === "4";

      if (
        signCallsForS132Prompt({
          formTemplateId: formDraft.formTemplate.id,
          part: formDraft.part,
          partCount: template.parts.length,
          formVersion: formDraft.formVersion,
          isOnePartRecordOfDetention: isOnePartH3Form,
        })
      ) {
        queueS132Prompt(user, formDraft.formContextId, "detention");
      }

      const partTemplateSignatureSuccessMessage =
        template.parts[formDraft.part - 1]?.overrideSignatureSuccessMessage;

      // `formDraft.part` is 1-based, so this is the part *after* the one signed.
      const nextPartTemplate = template.parts[formDraft.part];

      // Is the form going to be complete?
      if (
        template.parts.length === formDraft.part ||
        nextPartTemplate?.autoAssignToPartSignatory
      ) {
        if (formDraft.formContext.type === "admission") {
          // We're signing an H3
          if (
            recordOfDetentionTemplateIds.includes(formDraft.formTemplate.id)
          ) {
            const signedApplication = formDraft.formContext.forms.find((f) =>
              applicationTemplateIds.includes(f.formTemplate.id),
            );
            const applicationTemplate = baseTemplates.find(
              (t) => t.id === signedApplication?.formTemplate.id,
            );

            if (user.sessionContext?.teamType === "mha") {
              if (isASection4H3) {
                navigate(
                  routeFns.formContextPageSuccessDialog(
                    formDraft.formContextId,
                    formDraft.patientId,
                    {
                      title: formSuccessfullySignedTitle,
                      message: `You have successfully signed ${partText} of ${fullFormName}. Close to complete part ${
                        formDraft.part + 1
                      }`,
                    },
                  ),
                );
              } else {
                navigate(
                  routeFns.formContextPageSuccessDialog(
                    formDraft.formContextId,
                    formDraft.patientId,
                    {
                      title: formSuccessfullySignedTitle,
                      message: `You have successfully signed Section ${applicationTemplate?.section} - ${template.description}. Close to mark administrative scrutiny of this assessment complete.`,
                    },
                  ),
                );
              }
            } else {
              if (isASection4H3) {
                navigate(
                  routeFns.formContextPageSuccessDialog(
                    formDraft.formContextId,
                    formDraft.patientId,
                    {
                      title: formSuccessfullySignedTitle,
                      message: `You have successfully signed ${partText} of ${fullFormName}. Close to complete part ${
                        formDraft.part + 1
                      }`,
                    },
                  ),
                );
              } else if (willAutoRoute) {
                navigate(
                  routeFns.formContextPageSendRequest(
                    formDraft.formContextId,
                    formDraft.patientId,
                    {
                      requestType: "finalise",
                      title: `You have successfully signed Section ${applicationTemplate?.section} - ${template.description}. This form has been automatically sent to your MHL team(s) for review.`,
                      infoMessage:
                        "Would you like to share this form with any other teams?",
                    },
                  ),
                );
              } else {
                navigate(
                  routeFns.formContextPageSendRequest(
                    formDraft.formContextId,
                    formDraft.patientId,
                    {
                      requestType: "finalise",
                      title: `You have successfully signed Section ${applicationTemplate?.section} - ${template.description}. Send Section ${applicationTemplate?.section} admission forms to local MHA office for scrutiny.`,
                    },
                  ),
                );
              }
            }
          } else if (formDraft.formVersion > 1) {
            navigate(
              routeFns.formContextPageSuccessDialog(
                formDraft.formContextId,
                formDraft.patientId,
                {
                  title: "Amend successfully confirmed and sent back",
                  message:
                    "The requesting team have been notified by email that you have confirmed the amend so you don't have to send it back.",
                },
              ),
            );
          }

          // We're signing an application
          else if (applicationTemplateIds.includes(formDraft.formTemplate.id)) {
            navigate(
              routeFns.formContextPageSendRequest(
                formDraft.formContextId,
                formDraft.patientId,
                {
                  requestType: "record-of-detention",
                  title: `You have successfully signed Section ${template.section} - ${template.description}. Invite another user or team to complete the record of detention form.`,
                  infoMessage:
                    "Record of detention form to be completed by someone acting on behalf of the responsible hospital.",
                },
              ),
            );
          }

          // We're signing a med rec and it's going to be complete
          else {
            // Check if this is an independent S12 doctor with the feature enabled
            const isIndependentS12Doctor =
              isIndependentUserSession(user) &&
              user.sessionOrganisationConfiguration != null &&
              canDoctorInitiateS12Payments(
                user.sessionOrganisationConfiguration,
              ) &&
              medicalRecommendationTemplateIds.includes(
                formDraft.formTemplate.id,
              );

            // If I was requested to do this med rec by somebody then just show a success dialog instead as they'll have been notified
            if (
              formDraft.formContext.workItems.find(
                (w) => w.teamId === user.sessionContext!.teamId,
              )?.requestType === "medical-recommendation"
            ) {
              // For independent S12 doctors, redirect to payment request dialog
              if (isIndependentS12Doctor) {
                navigate(
                  routeFns.formContextS12PaymentRequest(
                    formDraft.formContextId,
                    formDraft.patientId,
                    { outcomeType: "form", formId: formDraft.formId },
                  ),
                );
              } else {
                navigate(
                  routeFns.formContextPageSuccessDialog(
                    formDraft.formContextId,
                    formDraft.patientId,
                    {
                      title: "Form successfully signed and sent back",
                      message: `You have successfully signed Section ${template.section} - ${template.description}. The requesting team or user have been notified by email that you have signed this form so you don't have to send it back.`,
                    },
                  ),
                );
              }
            } else {
              navigate(
                routeFns.formContextPageSendRequest(
                  formDraft.formContextId,
                  formDraft.patientId,
                  {
                    title: `You have successfully signed Section ${template.section} - ${template.description}. Send form to another user or team to work on this assessment.`,
                  },
                ),
              );
            }
          }
        } else {
          // Standalone form
          // Form is complete
          if (template.parts.length === formDraft.part || isOnePartH3Form) {
            // An MCA form may have a partner form to follow, and the invitation to
            // start it lives on the assessment, so land the user there rather than
            // on the send-for-scrutiny step. Sending remains available from there.
            if (formDraft.formContextType === "mca-assessment") {
              navigate(
                routeFns.formContextPageSuccessDialog(
                  formDraft.formContextId,
                  formDraft.patientId,
                  {
                    title: formSuccessfullySignedTitle,
                    message: `You have successfully signed ${fullFormName}.`,
                  },
                ),
              );
            }
            // If the user signing the last part of the form is an MHA, just prompt them to close and finalise
            else if (user.sessionContext?.teamType === "mha") {
              navigate(
                routeFns.formContextPageSuccessDialog(
                  formDraft.formContextId,
                  formDraft.patientId,
                  {
                    title: formSuccessfullySignedTitle,
                    message: `You have successfully signed ${
                      isOnePartH3Form
                        ? fullFormName
                        : isMultiPart
                          ? partText
                          : fullFormName
                    }. Close to mark administrative scrutiny of this assessment complete.`,
                  },
                ),
              );
            } else if (template.autoFinalise) {
              navigate(
                routeFns.formContextPageSuccessDialog(
                  formDraft.formContextId,
                  formDraft.patientId,
                  {
                    title: formSuccessfullySignedTitle,
                    message: `You have successfully signed ${formAndPartName}. This form is now complete and has been moved to your archive.`,
                  },
                ),
              );
            } else if (willAutoRoute) {
              navigate(
                routeFns.formContextPageSendRequest(
                  formDraft.formContextId,
                  formDraft.patientId,
                  {
                    requestType: "finalise",
                    title: `You have successfully signed ${
                      isMultiPart ? partText : fullFormName
                    }. This form has been automatically sent to your MHL team(s) for review.`,
                    infoMessage:
                      "Would you like to share this form with any other teams?",
                  },
                ),
              );
            } else {
              const signatureSuccessMessage =
                partTemplateSignatureSuccessMessage
                  ? {
                      infoMessage: partTemplateSignatureSuccessMessage,
                    }
                  : {};
              navigate(
                routeFns.formContextPageSendRequest(
                  formDraft.formContextId,
                  formDraft.patientId,
                  {
                    requestType: "finalise",
                    title: `You have successfully signed ${
                      isMultiPart ? partText : fullFormName
                    }. ${
                      partTemplateSignatureSuccessMessage
                        ? ""
                        : `Send ${
                            isMultiPart ? fullFormName : "form"
                          } to your local MHA office for scrutiny.`
                    }`,
                    ...signatureSuccessMessage,
                  },
                ),
              );
            }
          } else if (isASection4H3) {
            navigate(
              routeFns.formContextPageSuccessDialog(
                formDraft.formContextId,
                formDraft.patientId,
                {
                  title: formSuccessfullySignedTitle,
                  message: `You have successfully signed ${partText} of ${fullFormName}. Close to complete part ${
                    formDraft.part + 1
                  }`,
                },
              ),
            );
          }
          // Auto-assign (e.g. part 3 of CTO1)
          else {
            const currentForm = formDraft.myWorkItem?.forms.find(
              (f) => f.id === formId,
            );
            const latestVersion = getLatestVersion(currentForm);

            const latestPrevPartFormDraft = formDraft?.previousPartsFormData[
              template.parts[formDraft.part]?.autoAssignToPartSignatory! - 1
            ]
              ? formDraft.part - 1
              : null;
            const autoAssignBackSignatory = latestVersion?.signatures.find(
              (s) => s.part === latestPrevPartFormDraft,
            )!;

            const autoAssignUser = `${autoAssignBackSignatory?.userName} (${autoAssignBackSignatory?.teamName})`;

            const message = `Form successfully sent back to ${autoAssignUser}`;

            renderSuccessToast({
              message: message,
            });

            navigate(
              routeFns.formContextPageSuccessDialog(
                formDraft.formContextId,
                formDraft.patientId,
                {
                  title: "Form successfully signed and sent back",
                  message: `You have successfully signed ${formAndPartName}. The requesting team or user have been notified by email that you have signed this form so you don't have to send it back.`,
                },
              ),
            );
          }
        }
      } else {
        const friendlyRequirement =
          nextPartTemplate?.signing.userOrTeamRequirement.friendlyDescription;

        const friendlyRequirementMessage = friendlyRequirement
          ? {
              infoMessage: `Part ${
                formDraft.part + 1
              } to be completed by ${getBannerDescriptionOfFriendlyRequirement(
                friendlyRequirement,
              )}.`,
            }
          : {};

        if (formDraft.formContextType === "standalone") {
          const nextPartSignMessage = `You have successfully signed part ${
            formDraft.part
          }. Send Form ${template.formName}: Section ${template.section} - ${
            template.description
          } to another user or team to complete part ${formDraft.part + 1}`;

          navigate(
            routeFns.formContextPageSendRequest(
              formDraft.formContextId,
              formDraft.patientId,
              {
                formId: formDraft.formId,
                requestType: "complete",
                formPart: formDraft.part.toString(),
                title: nextPartSignMessage,
                ...friendlyRequirementMessage,
              },
            ),
          );
        } else {
          // A redo replacement is re-signed part-for-part by the doctors who
          // signed the original, so the server hands the next part straight to
          // that doctor — as it does for an amend. Nothing is left to send.
          const isRedoReplacement = !!formDraft.formContext.forms.find(
            (f) => f.id === formDraft.formId,
          )?.redoneFromFormId;

          if (formDraft.formVersion > 1 || isRedoReplacement) {
            navigate(
              routeFns.formContextPageSuccessDialog(
                formDraft.formContextId,
                formDraft.patientId,
                {
                  title: isRedoReplacement
                    ? t(
                        "pages.formDraftSignPage.secondPractitionerNotified.redoTitle",
                      )
                    : t(
                        "pages.formDraftSignPage.secondPractitionerNotified.amendTitle",
                      ),
                  message: isRedoReplacement
                    ? t(
                        "pages.formDraftSignPage.secondPractitionerNotified.redoMessage",
                      )
                    : t(
                        "pages.formDraftSignPage.secondPractitionerNotified.amendMessage",
                      ),
                },
              ),
            );
          } else {
            const nextPartSignMessage = `You have successfully signed part ${
              formDraft.part
            }. Send Section ${template.section} - ${
              template.description
            } to another user or team to complete part ${formDraft.part + 1}`;

            // Check if this is an independent S12 doctor with the feature enabled
            const isIndependentS12Doctor =
              isIndependentUserSession(user) &&
              user.sessionOrganisationConfiguration != null &&
              canDoctorInitiateS12Payments(
                user.sessionOrganisationConfiguration,
              ) &&
              medicalRecommendationTemplateIds.includes(
                formDraft.formTemplate.id,
              );

            navigate(
              routeFns.formContextPageSendRequest(
                formDraft.formContextId,
                formDraft.patientId,
                {
                  formId: formDraft.formId,
                  requestType: "complete",
                  formPart: "1",
                  title: nextPartSignMessage,
                  ...friendlyRequirementMessage,
                  // For independent S12 doctors, open payment request dialog after sending
                  ...(isIndependentS12Doctor
                    ? {
                        openS12PaymentRequest: "true",
                        s12OutcomeType: "form",
                        s12FormId: formDraft.formId,
                      }
                    : {}),
                },
              ),
            );
          }
        }
      }
    } else {
      const reason = (result.data as unknown as { reason?: string })?.reason;
      renderErrorToast({ message: reason ?? "Signing failed" });
    }
  }
};
